import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { TabsBar } from '@/common/TabsBar';
import { useCourseStudyAccess } from '~/hooks/useCourseStudyAccess';

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/course-study',
)({
  component: MemberCourseStudyRoute,
});

export default function MemberCourseStudyRoute() {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();
  const { access, isLoading } = useCourseStudyAccess(memberId);

  // Resolve before deciding anything. The tab SET is picked from the same answer
  // as the refusal, so an `access?.is_supervisor ? … : studentTabs` written
  // against an unresolved query hands a supervisor the student strip on the first
  // render and on every error — the bar flickers, and an errored load silently
  // drops Course Progression and Study Activities.
  if (isLoading) return null;

  // Positive test: `access` is undefined on a 401, a 500 or a dropped connection,
  // and every one of those is a refusal. An `access && !access.can_view` test
  // falls through on all three and renders the whole strip plus <Outlet/>.
  if (access?.can_view !== true) return <NotFoundScreen />;

  // Legacy's first slot is chosen by the viewer's RELATION to the displayed
  // student, never by the viewer's own profile type
  // (`class-course-study.php:28-49`); Quiz Submissions and Certificates are
  // appended unconditionally (`:51-59`).
  const supervisorTabs = [
    { title: t('lessons.course-progression'), to: '' },
    { title: t('lessons.study-activities'), to: 'study-activities' },
    { title: t('lessons.quiz-submissions'), to: 'quizzes' },
    { title: t('wbs.certificates'), to: 'certificates' },
  ];
  // Plural: legacy labels it `__('Courses', 'wbs')` (`class-course-study.php:44`).
  const studentTabs = [
    { title: t('lessons.courses'), to: '' },
    { title: t('lessons.quiz-submissions'), to: 'quizzes' },
    { title: t('wbs.certificates'), to: 'certificates' },
  ];

  return (
    <Container>
      <TabsBar
        tabs={access.is_supervisor ? supervisorTabs : studentTabs}
        base={'/members/$memberId/course-study'}
        tabLevel={4}
      />
      <MainContent>
        <Outlet />
      </MainContent>
    </Container>
  );
}
const Container = styled.div`
  padding-top: 6px;
`;

const MainContent = styled.main`
  background: var(--color-background);
  flex: 1;
  min-width: 0;
`;
